import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Tab,
    Tabs,
    Theme,
} from '@mui/material';
import type CropperJS from 'cropperjs';
import 'cropperjs/dist/cropper.css';
import { compressAccurately } from 'image-conversion';
import { createStyles, makeStyles } from '@mui/styles';
import DialogComponent from '../dialog/dialog.component';
import WaitButton from '../button/waitButton.component';
import GlobalNotification from '../common/snackbar';
import i18n from '../../i18n';
import { apiManage_rbp } from '../../request';
import { LIMIT_FILE_SIZE } from '../../constants/base.contants';
import {
    blobToImageFile,
    createEditableImageSource,
    DEFAULT_PIXABAY_LANGUAGE,
    EDIT_IMAGE_MIME,
    isImageFileWithinLimit,
    readImageFileAsDataUrl,
    resolvePixabayFetchUrl,
} from '../../utils/image-file';
import type { CloudDishLibraryPixabayHit } from '../../request/api/rbp/cloud-dish-library';
import type {
    ImageSourcePickerDialogProps,
    ImageSourcePickerTab,
} from './image-source-picker.types';
import {
    getImageBackgroundColor,
    getImageOutputDimensions,
} from '../../utils/image-transform';
import type { ImageBackgroundColor, ImageEditMode } from '../../utils/image-transform';
import themeTypography from '../../themes/typography';
import NetworkImageSourcePanel from './network-image-source-panel.component';
import ImageCropEditor from './image-crop-editor.component';

/** 网络搜图网格每页条数（3 列 × 2 行） */
const PIXABAY_PER_PAGE = 6;

/** 裁切确认目标体积上限（KB），与既有 CropperDialog 一致 */
const CROP_TARGET_KB = 300;

/** 编辑结果 JPEG 编码质量，避免以无损质量导出后重复压缩。 */
const EDIT_OUTPUT_QUALITY = 0.92;

/**
 * 将原文件名后缀统一为 jpg，匹配最终 JPEG 输出格式。
 * @param name - 原始文件名
 * @returns JPEG 文件名
 */
function toJpegFileName(name: string): string {
    if (!name) {
        return `image-${Date.now()}.jpg`;
    }
    return `${name.replace(/\.[^./\\]+$/, '')}.jpg`;
}

const typography = themeTypography();

/**
 * 按填充或适应模式将编辑源绘制到目标画布。
 * @param dataUrl - 编辑源图片地址
 * @param editMode - 图片编辑模式
 * @param aspectRatio - 目标宽高比
 * @param backgroundColor - 适应模式背景色
 * @returns 目标图片 Blob
 */
async function renderImageMode(
    dataUrl: string,
    editMode: Exclude<ImageEditMode, 'crop'>,
    aspectRatio: number,
    backgroundColor: ImageBackgroundColor,
    rotate: number,
): Promise<Blob> {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
        const element = new Image();
        element.onload = () => resolve(element);
        element.onerror = () => reject(new Error('image-load-failed'));
        element.src = dataUrl;
    });
    const dimensions = getImageOutputDimensions(image.naturalWidth, image.naturalHeight, aspectRatio);
    if (!dimensions) {
        throw new Error('image-dimensions-invalid');
    }
    const canvas = document.createElement('canvas');
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('canvas-context-unavailable');
    }
    if (editMode === 'fit') {
        context.fillStyle = getImageBackgroundColor(backgroundColor);
        context.fillRect(0, 0, canvas.width, canvas.height);
    }
    const scale =
        editMode === 'fill'
            ? Math.max(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight)
            : Math.min(canvas.width / image.naturalWidth, canvas.height / image.naturalHeight);
    const drawWidth = image.naturalWidth * scale;
    const drawHeight = image.naturalHeight * scale;
    context.save();
    context.translate(canvas.width / 2, canvas.height / 2);
    context.rotate((rotate * Math.PI) / 180);
    context.drawImage(
        image,
        -drawWidth / 2,
        -drawHeight / 2,
        drawWidth,
        drawHeight,
    );
    context.restore();
    return canvasToBlob(canvas);
}

/**
 * 将 Canvas 内容转换为图片 Blob。
 * @param canvas - 待导出的 Canvas
 * @returns Canvas 导出的 JPEG 图片 Blob
 */
function canvasToBlob(canvas: HTMLCanvasElement): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
        canvas.toBlob(
            (blob) => (blob ? resolve(blob) : reject(new Error('canvas-blob-failed'))),
            EDIT_IMAGE_MIME,
            EDIT_OUTPUT_QUALITY,
        );
    });
}

/**
 * 将编辑结果压缩到既有目标体积附近。
 * @param blob - 编辑后的图片 Blob
 * @returns 压缩后的图片 Blob
 */
async function compressEditedImage(blob: Blob): Promise<Blob> {
    if (blob.size / 1024 <= CROP_TARGET_KB) {
        return blob;
    }
    return (await compressAccurately(blob, CROP_TARGET_KB)) as Blob;
}

const useStyles = makeStyles<Theme, {}>(() =>
    createStyles({
        /** Tab 下内容区 */
        body: {
            minHeight: 672,
            backgroundColor: 'var(--fill-neutral-100)',
        },
        /** 本地空态上传区域 */
        emptyLocal: {
            width: 480,
            height: 480,
            borderRadius: 8,
            backgroundColor: 'var(--fill-primary-200)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
        },
        /** 本地上传空态文案 */
        emptyLocalText: {
            color: 'var(--text-primary-300)',
            ...typography.subtitle1,
        },
        /** 裁切区 + 右侧说明 */
        cropLayout: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
        },
        /** 左侧裁切主区 */
        cropMain: {
            width: '100%',
            maxWidth: 816,
            flexShrink: 0,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
        },
        /** 图片说明与模式切换行 */
        cropHeader: {
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            flexWrap: 'wrap',
        },
        /** 裁切标题右侧操作按钮组。 */
        cropHeaderActions: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginLeft: 'auto',
            flexShrink: 0,
        },
        /** 顶部裁切按钮，使用设计稿主题色实心样式。 */
        cropModeButton: {
            '&&': {
                minWidth: 64,
                height: 40,
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid var(--stroke-neutral-100)',
                color: 'var(--text-light)',
                backgroundColor: 'var(--fill-primary-300)',
                textTransform: 'none',
                ...typography.body2,
                '&:hover': {
                    borderColor: 'var(--stroke-neutral-100)',
                    color: 'var(--text-light)',
                    backgroundColor: 'var(--fill-primary-300)',
                },
            },
        },
        /** 右侧提示栏 */
        cropSide: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: 24,
            alignItems: 'stretch',
            flexWrap: 'wrap',
        },
        /** 本地上传说明文案 */
        cropDescription: {
            color: 'var(--text-secondary)',
            minWidth: 0,
            flex: '1 1 auto',
            ...typography.body2,
        },
        /** 图片来源 Tab 容器 */
        sourceTabs: {
            minHeight: 32,
            height: 32,
            '& .MuiTabs-flexContainer': {
                gap: 24,
            },
            '& .MuiTabs-indicator': {
                height: 4,
                borderRadius: 4,
            },
        },
        /** 图片来源 Tab */
        sourceTab: {
            minWidth: 120,
            minHeight: 32,
            height: 32,
            padding: 0,
            textTransform: 'none',
            ...typography.body1,
            textAlign: 'center',
            /** 提高选择器优先级，覆盖 MUI Tab 默认的 white-space: normal */
            '&&': {
                whiteSpace: 'nowrap',
            },
            '&:not(.Mui-selected)': {
                color: 'var(--text-dark)',
                ...typography.body1,
            },
            '&.Mui-selected': {
                color: 'var(--text-primary-300)',
                ...typography.subtitle1,
            },
        },
        /** 裁切预览容器 */
        cropPreview: {
            width: 640,
            height: 400,
            maxWidth: '100%',
            borderRadius: 8,
            border: '2px solid var(--stroke-primary-300)',
            overflow: 'hidden',
            backgroundColor: 'var(--fill-primary-200)',
            position: 'relative',
        },
        /** 读取与降采样大图期间的遮罩。 */
        sourceLoading: {
            position: 'absolute',
            inset: 0,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--overlay-800-a25)',
        },
        /** 重新选择按钮 */
        reselectButton: {
            '&&': {
                minWidth: 88,
                height: 40,
                padding: '8px 16px',
                borderRadius: 8,
                color: 'var(--text-primary-300)',
                border: '1px solid var(--stroke-primary-300)',
                backgroundColor: 'var(--fill-neutral-200)',
                textTransform: 'none',
                ...typography.body2,
                '&:hover': {
                    color: 'var(--text-primary-300)',
                    borderColor: 'var(--stroke-primary-300)',
                    backgroundColor: 'var(--fill-neutral-200)',
                },
            },
        },
        /** 旋转/重置工具条 */
        cropTools: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 24,
            flexWrap: 'wrap',
        },
        /** 裁切操作按钮 */
        cropToolButton: {
            '&&': {
                minWidth: 112,
                height: 48,
                padding: '8px 16px',
                borderRadius: 8,
                border: '1px solid var(--stroke-primary-200)',
                color: 'var(--text-dark)',
                ...typography.body2,
                '& .MuiButton-startIcon': {
                    marginRight: 8,
                },
                '@media (max-width: 900px)': {
                    minWidth: 0,
                    flex: 1,
                },
            },
        },
        /** 重置按钮比旋转按钮宽 16px，与设计稿工具栏对齐。 */
        cropToolResetButton: {
            '&&': {
                minWidth: 128,
            },
        },
        /** 适应模式的黑白背景切换按钮组。 */
        backgroundColorButtons: {
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            height: 32,
        },
        /** 右侧信息卡片 */
        infoCard: {
            width: 'calc(50% - 12px)',
            order: 3,
            boxSizing: 'border-box',
            border: '1px solid var(--stroke-neutral-800-a25)',
            borderRadius: 8,
            padding: '8px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            color: 'var(--text-dark)',
            ...typography.body2,
        },
        /** 右侧提示卡片 */
        tipCard: {
            width: '100%',
            order: 1,
            boxSizing: 'border-box',
            border: '1px solid var(--stroke-neutral-800-a25)',
            borderRadius: 8,
            padding: '7px 16px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'center',
            gap: 8,
            color: 'var(--text-dark)',
            ...typography.body2,
        },
        /** 右侧建议卡片 */
        suggestionCard: {
            width: 'calc(50% - 12px)',
            order: 2,
            boxSizing: 'border-box',
            borderRadius: 8,
            padding: '8px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            backgroundColor: 'var(--fill-neutral-200)',
            color: 'var(--text-dark)',
            ...typography.body2,
        },
        /** 侧栏卡片标题 */
        cardTitle: {
            color: 'var(--text-primary-300)',
            ...typography.subtitle2,
        },
        /** 图片信息左右行 */
        infoRow: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            minWidth: 0,
        },
        /** 图片信息标签 */
        infoLabel: {
            flexShrink: 0,
        },
        /** 图片信息值 */
        infoValue: {
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'right',
        },
        /** 提示/建议标题行 */
        cardTitleRow: {
            display: 'flex',
            alignItems: 'center',
            gap: 8,
        },
        /** 提示正文 */
        tipText: {
            whiteSpace: 'normal',
            flex: '0 1 auto',
        },
        /** 建议列表 */
        suggestionList: {
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
        },
        /** 弹窗底部等待按钮尺寸与文字排版 */
        footerWaitButton: {
            minWidth: 200,
            height: 48,
            '& .MuiButton-root': {
                width: '100%',
                height: '100%',
                ...typography.h4,
                textTransform: 'none',
            },
        },
        /** 网络区表单项间距（Figma：区块间距 24） */
        networkSection: {
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            width: '100%',
        },
        /** 粘贴链接 / 搜图单块（标题 + 输入行） */
        networkBlock: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            width: '100%',
        },
        /** 网络区块标题（正文-加粗） */
        networkBlockTitle: {
            color: 'var(--text-dark)',
            ...typography.subtitle1,
        },
        /** 粘贴链接 / 搜图输入行（Figma：输入 632 + 间距 24 + 按钮 160 = 816） */
        urlRow: {
            display: 'flex',
            gap: 24,
            alignItems: 'stretch',
            width: '100%',
            boxSizing: 'border-box',
        },
        /**
         * 网络输入框容器：占满按钮左侧剩余宽度（设计稿约 632），高度 56。
         * 不用写死 632，避免滚动条占宽后输入被挤换行。
         */
        networkInputWrap: {
            flex: '1 1 0',
            minWidth: 0,
            height: 56,
            '& .MuiFormControl-root': {
                width: '100%',
                height: '100%',
            },
            '& .MuiOutlinedInput-root': {
                height: 56,
                boxSizing: 'border-box',
                borderRadius: 8,
                ...typography.body1,
            },
            '& .MuiOutlinedInput-input': {
                padding: '16px',
                height: 'auto',
                boxSizing: 'border-box',
                ...typography.body1,
            },
        },
        /** 网络搜图语言下拉框，与关键词输入框和搜索按钮同高 */
        networkLanguageSelect: {
            flex: '0 0 160px',
            width: 160,
            height: 56,
            '& .MuiFormControl-root': {
                width: '100%',
                height: '100%',
            },
            '& .MuiOutlinedInput-root': {
                height: 56,
                boxSizing: 'border-box',
                borderRadius: 8,
                ...typography.body1,
            },
        },
        /** 获取图片 / 搜索按钮（Figma：160×56） */
        networkActionButton: {
            '&&': {
                width: 160,
                minWidth: 160,
                maxWidth: 160,
                height: 56,
                flexShrink: 0,
                borderRadius: 8,
                textTransform: 'none',
                ...typography.body1,
                '& .MuiButton-startIcon': {
                    marginRight: 8,
                    marginLeft: 0,
                },
            },
        },
        /** 搜图结果网格：一行三列，每张图片 256×160，间距 24px。 */
        hitGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 24,
            width: '100%',
            boxSizing: 'border-box',
        },
        /** 搜图无结果时的空状态，保持在结果区域水平和垂直居中。 */
        emptySearchResult: {
            width: '100%',
            minHeight: 344,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--text-secondary)',
            ...typography.body1,
        },
        /** 搜图结果署名，放置在结果网格右下角 */
        attribution: {
            alignSelf: 'flex-end',
            textAlign: 'right',
        },
        /** 单格缩略图容器，图片比例为 256:160。 */
        hitCell: {
            position: 'relative',
            width: '100%',
            height: 160,
            aspectRatio: '256 / 160',
            minWidth: 0,
            borderRadius: 16,
            overflow: 'hidden',
            border: '4px solid transparent',
            boxSizing: 'border-box',
            backgroundColor: 'var(--fill-neutral-200)',
        },
        /** 搜图结果的选择按钮，覆盖整张缩略图。 */
        hitSelectButton: {
            '&&': {
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                padding: 0,
                borderRadius: 12,
            },
        },
        /** 选中态：主题色 4px 描边（对齐 Figma） */
        hitCellSelected: {
            '&&': {
                borderColor: 'var(--stroke-primary-300)',
            },
        },
        /** 缩略图铺满格子，圆角由外层 overflow 裁切 */
        hitImg: {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
        },
        /** 居中显示的图片预览按钮。 */
        hitPreviewButton: {
            '&&': {
                position: 'absolute',
                right: 8,
                bottom: 8,
                zIndex: 1,
                width: 40,
                height: 40,
                color: 'var(--fill-neutral-100)',
                backgroundColor: 'var(--overlay-800-a50)',
                '&:hover': {
                    backgroundColor: 'var(--overlay-800-a50)',
                },
            },
        },
        /** 中等尺寸图片预览内容。 */
        imagePreview: {
            display: 'block',
            width: '100%',
            maxWidth: '640px',
            maxHeight: '70vh',
            objectFit: 'contain',
            margin: '0 auto',
        },
        /** 粘贴链接下方预览：Figma 为 640×400、居中、圆角 16 且无描边 */
        pastePreview: {
            width: 640,
            height: 400,
            maxWidth: '100%',
            borderRadius: 16,
            overflow: 'hidden',
            boxSizing: 'border-box',
            backgroundColor: 'var(--fill-neutral-200)',
            margin: '24px auto 0',
        },
        /** 分页居中 */
        paginationWrap: {
            display: 'flex',
            justifyContent: 'center',
            marginTop: 0,
        },
        /** 隐藏 file input */
        fileInput: {
            display: 'none',
        },
        /** 窄屏时将左右布局改为上下布局，避免裁切区被弹窗裁掉 */
        responsiveCropLayout: {
            '@media (max-width: 900px)': {
                flexDirection: 'column',
            },
        },
        /** 窄屏时侧栏占满可用宽度 */
        responsiveCropSide: {
            '@media (max-width: 900px)': {
                width: '100%',
            },
        },
    }),
);

/**
 * 统一图片来源选择器：本地上传（裁切）/ 网络上传（粘贴预览或搜图选中后直接确认）。
 * 对齐 Figma「编辑菜单图片」；业务上传仍由外层负责。
 */
export default function ImageSourcePickerDialog(
    props: Readonly<ImageSourcePickerDialogProps>,
): React.ReactElement {
    const {
        open,
        onClose,
        onConfirm,
        uploadSpec,
        title,
        stringId: stringIdProp,
    } = props;
    const classes = useStyles();

    /** 网络能力餐厅码 */
    const stringId = String(stringIdProp || '').trim();
    /** 是否允许网络 Tab */
    const networkEnabled = Boolean(stringId);

    /** 当前 Tab */
    const [tab, setTab] = useState<ImageSourcePickerTab>('local');
    /** 编辑源 Object URL；图片已在进入编辑器前降采样 */
    const [imgSrc, setImgSrc] = useState('');
    /** 编辑源序号，用于避免将图片内容拼接为 Cropper key */
    const [cropSourceId, setCropSourceId] = useState(0);
    /** 是否正在读取并降采样图片 */
    const [sourceBusy, setSourceBusy] = useState(false);
    /** 源文件名（确认时写入 File） */
    const [sourceFileName, setSourceFileName] = useState('');
    /** 原图文件大小（KB），用于右侧图片信息展示 */
    const [sourceFileSizeKb, setSourceFileSizeKb] = useState(0);
    /** 当前编辑结果预计上传大小（KB）。 */
    const [estimatedUploadSizeKb, setEstimatedUploadSizeKb] = useState<number | null>(null);
    /** 原图尺寸，格式为「宽*高」 */
    const [sourceImageSize, setSourceImageSize] = useState('');
    /** 旋转累计角度 */
    const [rotate, setRotate] = useState(0);
    /** 当前图片编辑模式，默认保留原有裁切行为。 */
    const [editMode, setEditMode] = useState<ImageEditMode>(
        props.initialEditMode || 'crop',
    );
    /** 适应模式背景色，默认黑色。 */
    const [backgroundColor, setBackgroundColor] = useState<ImageBackgroundColor>(
        props.initialBackgroundColor || 'black',
    );
    /** Cropper 实例 */
    const [cropper, setCropper] = useState<CropperJS>();
    /** 裁切区域变化序号，用于触发预计上传大小重新计算。 */
    const [cropRevision, setCropRevision] = useState(0);
    /** 当前图片读取请求序号，用于丢弃过期异步结果。 */
    const cropRequestIdRef = useRef(0);
    /** 本地 file input */
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    /** 当前编辑源 Object URL，替换或卸载时释放 */
    const cropObjectUrlRef = useRef('');

    /** 粘贴链接 */
    const [pasteUrl, setPasteUrl] = useState('');
    /** 粘贴链接拉取后的预览 DataURL（显示在输入行下方） */
    const [pastePreviewUrl, setPastePreviewUrl] = useState('');
    /** 粘贴链接拉取后待确认上传的 File */
    const [pasteFile, setPasteFile] = useState<File | null>(null);
    /** 搜图关键词 */
    const [searchQ, setSearchQ] = useState('');
    /** 搜图语言，默认使用意大利语 */
    const [searchLang, setSearchLang] = useState(DEFAULT_PIXABAY_LANGUAGE);
    /** 搜图页码 */
    const [page, setPage] = useState(1);
    /** 搜图命中 */
    const [hits, setHits] = useState<CloudDishLibraryPixabayHit[]>([]);
    /** 是否已经完成至少一次搜图，用于区分初始状态与无结果状态 */
    const [hasSearched, setHasSearched] = useState(false);
    /** 可返回命中上限 */
    const [totalHits, setTotalHits] = useState(0);
    /** 署名 */
    const [attribution, setAttribution] = useState('');
    /** 选中的 hit id（仅选中，确认时再拉取） */
    const [selectedHitId, setSelectedHitId] = useState<number | null>(null);
    /** 当前打开预览的中等尺寸图片 URL */
    const [previewImageUrl, setPreviewImageUrl] = useState('');
    /** 粘贴链接拉取 loading */
    const [pasteFetchBusy, setPasteFetchBusy] = useState(false);
    /** Pixabay 搜索 / 翻页 loading */
    const [searchBusy, setSearchBusy] = useState(false);

    /** 总页数 */
    const totalPages = useMemo(() => {
        if (!totalHits || !PIXABAY_PER_PAGE) {
            return 0;
        }
        return Math.max(1, Math.ceil(totalHits / PIXABAY_PER_PAGE));
    }, [totalHits]);

    /** 释放当前编辑源 Object URL，避免重复选图造成内存累积。 */
    const releaseCropObjectUrl = useCallback(() => {
        if (cropObjectUrlRef.current) {
            URL.revokeObjectURL(cropObjectUrlRef.current);
            cropObjectUrlRef.current = '';
        }
    }, []);

    /** 组件卸载时释放编辑源。 */
    useEffect(() => releaseCropObjectUrl, [releaseCropObjectUrl]);

    /**
     * 打开时重置状态。
     */
    useEffect(() => {
        cropRequestIdRef.current += 1;
        if (!open) {
            releaseCropObjectUrl();
            setImgSrc('');
            setSourceBusy(false);
            return;
        }
        setTab('local');
        releaseCropObjectUrl();
        setImgSrc('');
        setSourceBusy(false);
        setSourceFileName('');
        setSourceFileSizeKb(0);
        setEstimatedUploadSizeKb(null);
        setSourceImageSize('');
        setRotate(0);
        setEditMode(props.initialEditMode || 'crop');
        setBackgroundColor(props.initialBackgroundColor || 'black');
        setCropper(undefined);
        setCropRevision(0);
        setPasteUrl('');
        setPastePreviewUrl('');
        setPasteFile(null);
        setSearchQ('');
        setSearchLang(DEFAULT_PIXABAY_LANGUAGE);
        setPage(1);
        setHits([]);
        setHasSearched(false);
        setTotalHits(0);
        setAttribution('');
        setSelectedHitId(null);
        setPasteFetchBusy(false);
        setSearchBusy(false);
    }, [open, releaseCropObjectUrl]);

    /**
     * 无餐厅码时若停留在网络 Tab，强制回到本地。
     */
    useEffect(() => {
        if (!networkEnabled && tab === 'network') {
            setTab('local');
        }
    }, [networkEnabled, tab]);

    /**
     * 清空粘贴链接预览与待上传文件。
     */
    const clearPastePreview = useCallback(() => {
        setPastePreviewUrl('');
        setPasteFile(null);
    }, []);

    /**
     * 清空本地裁切源，回到选图。
     */
    const clearCropSource = useCallback(() => {
        cropRequestIdRef.current += 1;
        releaseCropObjectUrl();
        setImgSrc('');
        setSourceBusy(false);
        setEditMode('crop');
        setRotate(0);
        setCropper(undefined);
        setEstimatedUploadSizeKb(null);
    }, [releaseCropObjectUrl]);

    /**
     * 切换图片来源时退出当前编辑态，避免编辑中的图片被误认为属于新来源。
     * @param nextTab - 用户要切换到的图片来源 Tab
     */
    const handleSourceTabChange = useCallback(
        (nextTab: ImageSourcePickerTab) => {
            if (nextTab === tab) {
                return;
            }
            clearCropSource();
            setTab(nextTab);
        },
        [clearCropSource, tab],
    );

    /**
     * 用 File/Blob 进入裁切。
     * @param file - 图片文件
     * @param preferredName - 可选文件名
     * @param options.skipSizeLimit - 兼容旧调用，当前不再做原图体积前置限制
     */
    const enterCropWithFile = useCallback(
        async (
            file: File | Blob,
            preferredName?: string,
        ) => {
            const requestId = cropRequestIdRef.current + 1;
            cropRequestIdRef.current = requestId;
            setSourceBusy(true);
            try {
                const source = await createEditableImageSource(file);
                if (cropRequestIdRef.current !== requestId) {
                    return;
                }
                releaseCropObjectUrl();
                cropObjectUrlRef.current = URL.createObjectURL(source.blob);
                setImgSrc(cropObjectUrlRef.current);
                setCropSourceId((value) => value + 1);
                setSourceImageSize(`${source.naturalWidth}*${source.naturalHeight}`);
                setSourceFileName(
                    preferredName ||
                        (file instanceof File && file.name ? file.name : `image-${Date.now()}.jpg`),
                );
                setSourceFileSizeKb(Number((file.size / 1024).toFixed(2)));
                setEstimatedUploadSizeKb(null);
                setRotate(0);
            } catch (error) {
                console.error('read image file', error);
                if (cropRequestIdRef.current === requestId) {
                    releaseCropObjectUrl();
                    setImgSrc('');
                }
                GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
            } finally {
                if (cropRequestIdRef.current === requestId) {
                    setSourceBusy(false);
                }
            }
        },
        [releaseCropObjectUrl],
    );

    /**
     * 本地选文件。
     */
    const handleLocalFileChange = useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            // 允许重复选同一文件
            event.target.value = '';
            if (!file) {
                return;
            }
            if (!file.type.startsWith('image/')) {
                GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
                return;
            }
            await enterCropWithFile(file);
        },
        [enterCropWithFile],
    );

    /**
     * 粘贴链接拉取图片：在网络 Tab 下方预览，不跳转本地裁切。
     */
    const handleFetchFromPasteUrl = useCallback(async () => {
        const url = pasteUrl.trim();
        if (!url) {
            GlobalNotification.error(i18n.t('image_source_picker_text_paste_url_required'));
            return;
        }
        // 仅允许 http(s)，避免脏协议直接打后端
        if (!/^https?:\/\//i.test(url)) {
            GlobalNotification.error(i18n.t('image_source_picker_text_paste_url_invalid'));
            return;
        }
        if (!stringId) {
            GlobalNotification.error(i18n.t('image_source_picker_text_network_disabled'));
            return;
        }
        setPasteFetchBusy(true);
        try {
            const blob = await apiManage_rbp.post_cloud_dish_library_images_fetch_from_url({ url }, stringId);
            if (!blob || blob.size <= 0) {
                GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
                return;
            }
            const file = blobToImageFile(blob, `url-${Date.now()}.jpg`);
            const dataUrl = await readImageFileAsDataUrl(file);
            if (!dataUrl) {
                GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
                return;
            }
            // 粘贴预览与搜图选中互斥
            setSelectedHitId(null);
            setPasteFile(file);
            setPastePreviewUrl(dataUrl);
        } catch (e) {
            console.error('fetch-from-url', e);
            GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
        } finally {
            setPasteFetchBusy(false);
        }
    }, [pasteUrl, stringId]);

    /**
     * 执行 Pixabay 搜索。
     * @param nextPage - 页码
     * @param qOverride - 可选覆盖关键词
     */
    const runPixabaySearch = useCallback(
        async (nextPage: number, qOverride?: string) => {
            const q = String(qOverride ?? searchQ).trim();
            if (!q) {
                GlobalNotification.error(i18n.t('image_source_picker_text_search_q_required'));
                return;
            }
            if (!stringId) {
                GlobalNotification.error(i18n.t('image_source_picker_text_network_disabled'));
                return;
            }
            setSearchBusy(true);
            try {
                const res = await apiManage_rbp.get_cloud_dish_library_images_pixabay_search(
                    {
                        q,
                        lang: searchLang,
                        page: nextPage,
                        per_page: PIXABAY_PER_PAGE,
                        safesearch: true,
                        orientation: 'horizontal',
                    },
                    stringId,
                );
                if (res && typeof res === 'object' && (res as { error?: boolean }).error) {
                    GlobalNotification.error(i18n.t('image_source_picker_text_search_failed'));
                    return;
                }
                // 搜图结果列表，接口异常或缺少 hits 时按空数组处理。
                const nextHits = Array.isArray(res?.hits) ? res.hits : [];
                setHits(nextHits);
                setHasSearched(true);
                if (nextHits.length === 0) {
                    GlobalNotification.info(i18n.t('general_text_no_data'));
                }
                setTotalHits(Number(res?.totalHits) || 0);
                setAttribution(String(res?.attribution || ''));
                setPage(Number(res?.page) || nextPage);
                setSelectedHitId(null);
                // 新一轮搜图时清掉粘贴预览，避免两种来源同时有效
                clearPastePreview();
            } catch (e) {
                console.error('pixabay-search', e);
                GlobalNotification.error(i18n.t('image_source_picker_text_search_failed'));
            } finally {
                setSearchBusy(false);
            }
        },
        [clearPastePreview, searchLang, searchQ, stringId],
    );

    /**
     * 选中网格缩略图（仅高亮；确认上传时再拉取原图）。
     * @param hit - Pixabay 命中
     */
    const handleSelectHit = useCallback(
        (hit: CloudDishLibraryPixabayHit) => {
            cropRequestIdRef.current += 1;
            clearPastePreview();
            setSelectedHitId(hit.id);
        },
        [clearPastePreview],
    );

    /**
     * 网络 Tab：第一次确认进入裁切，第二次确认由统一裁切逻辑完成上传。
     */
    const handleConfirmNetwork = useCallback(async () => {
        if (!stringId) {
            GlobalNotification.error(i18n.t('image_source_picker_text_network_disabled'));
            return;
        }
        if (pasteFile) {
            try {
                await enterCropWithFile(pasteFile, pasteFile.name);
            } catch (e) {
                console.error('confirm paste file', e);
                GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
            }
            return;
        }
        const hit = hits.find((item) => item.id === selectedHitId);
        const fetchUrl = hit ? resolvePixabayFetchUrl(hit) : '';
        if (!hit || !fetchUrl) {
            GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
            return;
        }
        const requestId = cropRequestIdRef.current + 1;
        cropRequestIdRef.current = requestId;
        try {
            const blob = await apiManage_rbp.post_cloud_dish_library_images_fetch_from_url(
                { url: fetchUrl },
                stringId,
            );
            if (!blob || blob.size <= 0) {
                GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
                return;
            }
            if (cropRequestIdRef.current !== requestId) {
                return;
            }
            const file = blobToImageFile(blob, `pixabay-${hit.id}.jpg`);
            await enterCropWithFile(file, file.name);
        } catch (e) {
            console.error('confirm pixabay fetch', e);
            GlobalNotification.error(i18n.t('image_source_picker_text_fetch_url_failed'));
        }
    }, [enterCropWithFile, hits, pasteFile, selectedHitId, stringId]);

    /**
     * 重置裁切框与旋转。
     */
    const handleResetCrop = useCallback(() => {
        setRotate(0);
        cropper?.reset();
        cropper?.setAspectRatio(uploadSpec.aspectRatio);
        setCropRevision((value) => value + 1);
    }, [cropper, uploadSpec.aspectRatio]);

    /**
     * 根据当前编辑状态异步计算预计上传大小。
     * 计算结果复用最终确认时的渲染与压缩规则，裁切拖动时做短暂防抖。
     */
    useEffect(() => {
        if (!imgSrc) {
            setEstimatedUploadSizeKb(null);
            return;
        }
        let cancelled = false;
        const timer = window.setTimeout(async () => {
            try {
                let renderedBlob: Blob;
                if (editMode !== 'crop') {
                    renderedBlob = await renderImageMode(
                        imgSrc,
                        editMode,
                        uploadSpec.aspectRatio,
                        backgroundColor,
                        rotate,
                    );
                } else {
                    if (!cropper) {
                        return;
                    }
                    const canvas = cropper.getCroppedCanvas();
                    if (!canvas) {
                        return;
                    }
                    renderedBlob = await canvasToBlob(canvas);
                }
                const outputBlob = await compressEditedImage(renderedBlob);
                if (!cancelled) {
                    setEstimatedUploadSizeKb(Number((outputBlob.size / 1024).toFixed(2)));
                }
            } catch (error) {
                if (!cancelled) {
                    console.error('estimate image upload size', error);
                    setEstimatedUploadSizeKb(null);
                }
            }
        }, 250);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [backgroundColor, cropRevision, cropper, editMode, imgSrc, rotate, uploadSpec.aspectRatio]);

    /**
     * 确认裁切并回传。
     */
    const handleConfirmCrop = useCallback(async () => {
        if (!imgSrc) {
            return;
        }
        if (editMode !== 'crop') {
            try {
                const renderedBlob = await renderImageMode(
                    imgSrc,
                    editMode,
                    uploadSpec.aspectRatio,
                    backgroundColor,
                    rotate,
                );
                const outBlob = await compressEditedImage(renderedBlob);
                if (!isImageFileWithinLimit(outBlob)) {
                    GlobalNotification.error(
                        i18n.t('input_rule_text_please_enter_file_size_cannot_exceed_x', {
                            x: `${LIMIT_FILE_SIZE / 1024}k`,
                        }),
                    );
                    return;
                }
                const file = new File(
                    [outBlob],
                    toJpegFileName(sourceFileName),
                    { type: outBlob.type || EDIT_IMAGE_MIME },
                );
                const finalPreview = await readImageFileAsDataUrl(outBlob);
                onConfirm({ previewUrl: finalPreview, file });
                onClose();
            } catch (error) {
                console.error('confirm image mode', error);
                GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
            }
            return;
        }
        if (!cropper) {
            return;
        }
        try {
            const canvas = cropper.getCroppedCanvas();
            if (!canvas) {
                GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
                return;
            }
            const outBlob = await compressEditedImage(await canvasToBlob(canvas));
            if (!isImageFileWithinLimit(outBlob)) {
                GlobalNotification.error(
                    i18n.t('input_rule_text_please_enter_file_size_cannot_exceed_x', {
                        x: `${LIMIT_FILE_SIZE / 1024}k`,
                    }),
                );
                return;
            }
            const file = new File(
                [outBlob],
                toJpegFileName(sourceFileName),
                { type: outBlob.type || EDIT_IMAGE_MIME },
            );
            const finalPreview = await readImageFileAsDataUrl(outBlob);
            onConfirm({ previewUrl: finalPreview, file });
            onClose();
        } catch (e) {
            console.error('confirm crop', e);
            GlobalNotification.error(i18n.t('general_message_image_type_incorrect_upload_failed'));
        }
    }, [
        backgroundColor,
        cropper,
        editMode,
        imgSrc,
        onClose,
        onConfirm,
        sourceFileName,
        rotate,
        uploadSpec.aspectRatio,
    ]);

    /** 弹窗标题 */
    const dialogTitle = title?.trim() || i18n.t('image_source_picker_text_title');

    /** 本地是否在裁切视图 */
    const inCrop = Boolean(imgSrc);
    /** 网络 Tab 是否已有可上传来源（粘贴预览或搜图选中） */
    const networkReady = Boolean(pasteFile || selectedHitId);
    /** 当前阶段是否允许点击底部确认按钮。 */
    const canConfirm = !sourceBusy && (tab === 'network' && !inCrop ? networkReady : inCrop);
    /** 网络来源是否处于待编辑阶段。 */
    const isNetworkEditing = tab === 'network' && inCrop;
    /** 底部确认按钮文案 key。 */
    const confirmButtonTextKey =
        isNetworkEditing || tab === 'local'
            ? 'image_source_picker_button_confirm_upload'
            : 'general_button_confirm';

    return (
        <DialogComponent
            maxWidth="md"
            fullWidth
            open={open}
            onClose={() => onClose()}
            PaperProps={{
                sx: {
                    width: '864px',
                    maxWidth: 'calc(100vw - 32px)',
                    backgroundColor: 'var(--fill-neutral-100)',
                    '& .dialog-footer': {
                        padding: '0 24px 24px',
                        gap: 3,
                    },
                },
            }}
            sx={{
                '.MuiDialog-container > .MuiPaper-root': {
                    background: 'var(--fill-neutral-100)',
                },
            }}
            dialogContentStyle={{
                padding: '0 24px 24px',
                // 避免纵向滚动条占宽导致网络搜图从 3 列挤成 2 列
                overflowX: 'hidden',
            }}
            defaultHeaderProps={{
                title: dialogTitle,
                onClose: () => onClose(),
            }}
            footer={
                <Box display="flex" justifyContent="flex-end" gap={3}>
                    <Button
                        color="primary"
                        variant="outlined"
                        sx={{
                            width: 200,
                            height: 48,
                            typography: 'h4',
                            textTransform: 'none',
                        }}
                        onClick={() => onClose()}
                    >
                        {i18n.t('general_button_cancel')}
                    </Button>
                    <Box className={classes.footerWaitButton}>
                        <WaitButton
                            color="primary"
                            variant="contained"
                            disabled={!canConfirm}
                            onClick={async () => {
                                if (tab === 'network' && !inCrop) {
                                    await handleConfirmNetwork();
                                    return;
                                }
                                await handleConfirmCrop();
                            }}
                            titleText=""
                        >
                            {i18n.t(confirmButtonTextKey)}
                        </WaitButton>
                    </Box>
                </Box>
            }
        >
            <Box className={classes.body}>
                <Tabs
                    className={classes.sourceTabs}
                    value={tab}
                    onChange={(_e, v: ImageSourcePickerTab) => {
                        if (v === 'network' && !networkEnabled) {
                            GlobalNotification.error(
                                i18n.t('image_source_picker_text_network_disabled'),
                            );
                            return;
                        }
                        handleSourceTabChange(v);
                    }}
                    sx={{ mb: 3 }}
                >
                    <Tab
                        className={classes.sourceTab}
                        value="local"
                        label={i18n.t('image_source_picker_tab_local')}
                    />
                    <Tab
                        className={classes.sourceTab}
                        value="network"
                        label={i18n.t('image_source_picker_tab_network')}
                        disabled={!networkEnabled}
                    />
                </Tabs>

                {tab === 'network' ? (
                    inCrop ? (
                        <ImageCropEditor
                            classes={classes}
                            imgSrc={imgSrc}
                            cropSourceId={cropSourceId}
                            sourceBusy={sourceBusy}
                            fileInputRef={fileInputRef}
                            onLocalFileChange={handleLocalFileChange}
                            cropper={cropper}
                            setCropper={setCropper}
                            uploadSpec={uploadSpec}
                            editMode={editMode}
                            setEditMode={setEditMode}
                            backgroundColor={backgroundColor}
                            setBackgroundColor={setBackgroundColor}
                            rotate={rotate}
                            setRotate={setRotate}
                            onReselect={clearCropSource}
                            onReset={handleResetCrop}
                            onCropChange={() => setCropRevision((value) => value + 1)}
                            sourceFileName={sourceFileName}
                            sourceFileSizeKb={sourceFileSizeKb}
                            estimatedUploadSizeKb={estimatedUploadSizeKb}
                            sourceImageSize={sourceImageSize}
                        />
                    ) : (
                        <NetworkImageSourcePanel
                            classes={classes}
                            pasteUrl={pasteUrl}
                            setPasteUrl={setPasteUrl}
                            pastePreviewUrl={pastePreviewUrl}
                            searchQ={searchQ}
                            setSearchQ={setSearchQ}
                            searchLang={searchLang}
                            setSearchLang={setSearchLang}
                            pasteFetchBusy={pasteFetchBusy}
                            searchBusy={searchBusy}
                            onFetchFromPasteUrl={() => {
                                void handleFetchFromPasteUrl();
                            }}
                            onSearch={(nextPage) => {
                                void runPixabaySearch(nextPage);
                            }}
                            hasSearched={hasSearched}
                            hits={hits}
                            selectedHitId={selectedHitId}
                            onSelectHit={handleSelectHit}
                            onPreview={setPreviewImageUrl}
                            attribution={attribution}
                            page={page}
                            totalPages={totalPages}
                        />
                    )
                ) : (
                    <>
                        <ImageCropEditor
                            classes={classes}
                            imgSrc={imgSrc}
                            cropSourceId={cropSourceId}
                            sourceBusy={sourceBusy}
                            fileInputRef={fileInputRef}
                            onLocalFileChange={handleLocalFileChange}
                            cropper={cropper}
                            setCropper={setCropper}
                            uploadSpec={uploadSpec}
                            editMode={editMode}
                            setEditMode={setEditMode}
                            backgroundColor={backgroundColor}
                            setBackgroundColor={setBackgroundColor}
                            rotate={rotate}
                            setRotate={setRotate}
                            onReselect={clearCropSource}
                            onReset={handleResetCrop}
                            onCropChange={() => setCropRevision((value) => value + 1)}
                            sourceFileName={sourceFileName}
                            sourceFileSizeKb={sourceFileSizeKb}
                            estimatedUploadSizeKb={estimatedUploadSizeKb}
                            sourceImageSize={sourceImageSize}
                        />
                    </>
                )}
            </Box>
            <DialogComponent
                open={Boolean(previewImageUrl)}
                onClose={() => setPreviewImageUrl('')}
                maxWidth="md"
                PaperProps={{
                    sx: {
                        maxWidth: '720px',
                    },
                }}
                defaultHeaderProps={{
                    title: i18n.t('image_source_picker_label_search'),
                    onClose: () => setPreviewImageUrl(''),
                }}
                footer={<></>}
            >
                {previewImageUrl ? (
                    <img
                        className={classes.imagePreview}
                        src={previewImageUrl}
                        alt={i18n.t('image_source_picker_label_search')}
                    />
                ) : null}
            </DialogComponent>
        </DialogComponent>
    );
}
